import React, { useState } from 'react';
import util from '../../styles/utilities.module.css';
import { useNavigate } from 'react-router-dom';
import { useGetUserImpactStoriesQuery } from '../../slices/credentialingApi';
import './ImpactStoriesPage.css';

const ImpactStoriesPage = () => {
  const navigate = useNavigate();
  const [expandedStory, setExpandedStory] = useState(null);
  const { 
    data: storiesData, 
    isLoading, 
    error 
  } = useGetUserImpactStoriesQuery();

  const toggleStoryExpansion = (storyId) => {
    setExpandedStory(expandedStory === storyId ? null : storyId);
  };

  if (isLoading) {
    return (
      <div className="impact-stories-page">
        <div className="impact-stories-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your impact stories...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="impact-stories-page">
        <div className="impact-stories-container">
          <div className={`${util.alert} ${util['alert--danger']}`}>
            <h2>Unable to Load Impact Stories</h2>
            <p>There was an error loading your impact stories.</p>
            <button 
              className={`${util.btn} ${util['btn--tint']} ${util['btn-sm']} back-button`}
              onClick={() => navigate('/credentialing')}
            >
              ← Back to Training
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { stories = [], total_count = 0 } = storiesData?.data || {};

  return (
    <div className="impact-stories-page">
      <div className="impact-stories-container">
        <div className="impact-stories-header">
          <button 
            className={`${util.btn} ${util['btn--tint']} ${util['btn-sm']} back-button`}
            onClick={() => navigate('/credentialing')}
          >
            ← Back to Credentialing
          </button>
          <h1 className="impact-stories-title">
            📈 My Impact Stories
          </h1>
          <p className="impact-stories-subtitle">
            Your submitted impact stories help us understand and celebrate our collective impact in the classroom.
          </p>
        </div>

        <div className="impact-stories-content">
          {total_count === 0 ? (
            <div className="no-stories">
              <div className="no-stories-icon">📝</div>
              <h3>No Impact Stories Yet</h3>
              <p>You haven't submitted any impact stories yet. Start documenting how ClassGenius tools help your students!</p>
              <button 
                className={`${util.btn} ${util['btn--accentGradient']} ${util.elevateHover}`}
                onClick={() => navigate('/credentialing/impact-story')}
              >
                📝 Submit Your First Impact Story
              </button>
            </div>
          ) : (
            <>
              <div className="stories-summary">
                <h2>Your Impact Stories ({total_count})</h2>
                <p>Stories you've submitted for the Impact Champion badge</p>
                <button 
                  className={`${util.btn} ${util['btn--successGradient']} ${util.elevateHover}`}
                  onClick={() => navigate('/credentialing/impact-story')}
                >
                  + Add Another Story
                </button>
              </div>

              <div className="stories-grid">
                {stories.map((story) => {
                  const isExpanded = expandedStory === story.id;
                  return (
                    <div key={story.id} className={`story-card ${isExpanded ? 'expanded' : ''}`}>
                      <div className="story-card-header">
                        <h3 className="story-title">{story.title}</h3>
                        <div className="story-grade">
                          Grade {story.grade_level}
                        </div>
                      </div>
                      
                      <div className="story-card-body">
                        <div className="story-meta">
                          <div className="story-date">
                            📅 Submitted {story.submission_date}
                          </div>
                          <div className="story-stats">
                            <span className="stat">
                              📝 {story.description_length} chars description
                            </span>
                            {/* Impact length removed with student_impact field */}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="story-content">
                            <div className="story-section">
                              <h4>📚 How I Used ClassGenius Tools</h4>
                              <p className="story-text">{story.description}</p>
                            </div>
                            {/* Student Impact & Outcomes section removed */}
                          </div>
                        )}
                      </div>
                      
                      <div className="story-card-footer">
                        <div className="badge-contribution">
                          ✅ Contributes to Impact Champion Badge
                        </div>
                        <button 
                          className={`${util.btn} ${util['btn--slateGradient']} ${util['btn-sm']} expand-button`}
                          onClick={() => toggleStoryExpansion(story.id)}
                        >
                          {isExpanded ? '📖 Hide Story' : '📖 Read Full Story'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        
      </div>
    </div>
  );
};

export default ImpactStoriesPage;